import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import FormGroupTile from "../components/core/basic/FormGroupTile";
import BulkTagsForm from "../components/core/blocks/BulkTagsForm";
import NiceModal from "@ebay/nice-modal-react";
import { EntityTagsFormModal } from "../modules/tags/components/EntityTagsFormModal";
import { useModuleEnabled } from "../redux/selectors/modules";
import { getScheduledBidChangeValue, TARGET_STATES } from "../components/custom/category/advertisements/MicroStores2";
import { useDateFormatter } from "../i18n/useDateFormatter";
import moment from "moment";
import { useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useAppSelector } from "../redux/hooks";
import { useDates } from "../dates/DatesContext";
import { clientToServerSorting } from "../components/table/utils";
import api from "../utils/api";
import { apiClient } from "../zodios/client";
import { formatCurrency } from "../utils/formatNumber";
import { successToast, errorToast } from "../utils/toasts";
import BulkAdPlanActionForm from "../modules/advertising/components/BulkAdPlanActionForm";
import { invalidateTargetQueries } from "../modules/perf_reports/api/target/invalidateTargetQueries";
import {
  getScheduledActionConflictRange,
  isScheduledActionOverlap,
} from "../modules/advertising/utils/scheduledActionOverlap";

const TARGET_TYPE_TO_LABEL_KEY = {
  target: "Target",
  keyword: "Keyword",
};

const BULK_ACTION_RECORD_LIMIT = 100000;

// An empty bid range field means no bound on that side
const parseBidOrNull = (value) => {
  const bid = parseFloat(value);
  return Number.isFinite(bid) ? bid : null;
};

/**
 * @param {object} [selectAllParams]
 * @param {import('@tanstack/react-table').ColumnFiltersState} [selectAllParams.filters]
 * @param {import('@tanstack/react-table').SortingState} [selectAllParams.sorting]
 * @param {string} [selectAllParams.grouping]
 * @param {string} [selectAllParams.search]
 */
const useTargetBulkActions = (
  selectedTargets,
  setSelectedTargets,
  targetType,
  targetData,
  isNTMAd = false,
  isTrellisManagedAd = true,
  selectAllParams = /** @type {{ filters: import('@tanstack/react-table').ColumnFiltersState, sorting: import('@tanstack/react-table').SortingState, grouping: string, search: string } | null} */ (
    null
  ),
) => {
  const [bidValue, setBidValue] = useState("");
  const [minBid, setMinBid] = useState("");
  const [maxBid, setMaxBid] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [bidBoostFormData, setBidBoostFormData] = useState({
    actionUnit: "percent",
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().add(2, "days").format("YYYY-MM-DD"),
    keepBoostedBid: true,
  });
  const [filledTags, setFilledTags] = useState([]);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const { t } = useTranslation(["advertising"]);
  const fmt = useDateFormatter("monthDay");
  const dateRangeFmt = useDateFormatter("medium");
  const queryClient = useQueryClient();
  const channel = useAppSelector((state) => state.channel);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const targetLabelKey = TARGET_TYPE_TO_LABEL_KEY[targetType] ?? "Target";
  const targetTypeLabel = t(($) => $.advertising.target_type[targetLabelKey]);
  const targetTypeLowercase = t(($) => $.advertising.target_type_lowercase[targetLabelKey]);
  const selectedCount = selectedTargets === "all" ? 2 : (selectedTargets?.length ?? 0);

  const bulkActionHandler = useMutation({
    mutationFn: async (payload) => {
      return await apiClient.bulkTargetAction(payload);
    },
    onSuccess: () => {
      setSelectedTargets();
      successToast({ message: t(($) => $.advertising["Bulk action submitted successfully"]) });
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      errorToast({ message: t(($) => $.advertising["Error submitting bulk action"]), error });
    },
  });

  const { start, end, preStart, preEnd } = useDates();

  const dispatchBulkIfSelectAll = (actionData) => {
    if (!selectAllParams) return false;
    bulkActionHandler.mutate({
      table: targetType,
      filters: selectAllParams.filters,
      ordering: clientToServerSorting(selectAllParams.sorting),
      marketplace: marketPlace,
      start_date: start.format("YYYY-MM-DD"),
      end_date: end.format("YYYY-MM-DD"),
      start_date_previous: preStart.format("YYYY-MM-DD"),
      end_date_previous: preEnd.format("YYYY-MM-DD"),
      grouping: selectAllParams.grouping,
      limit: BULK_ACTION_RECORD_LIMIT,
      search: selectAllParams.search ? selectAllParams.search.split(",") : undefined,
      data: {
        marketplace: marketPlace,
        target_type: targetType,
        channel: channel.currentChannel ?? "amazon",
        ...actionData,
      },
    });
    return true;
  };

  const importedTargetIds = useMemo(() => {
    return targetData?.keywords?.filter((target) => target.target_source === "imported")?.map((t) => t.id) ?? [];
  }, [targetData]);

  const skippedTargetCount = useMemo(() => {
    if (selectedTargets === "all") return 0;
    return selectedTargets?.filter((targetId) => importedTargetIds?.includes(targetId))?.length ?? 0;
  }, [importedTargetIds, selectedTargets]);

  const customTags = useMemo(() => {
    return [...new Set(targetData?.keywords?.flatMap((target) => target.tags ?? []).filter((tag) => tag))];
  }, [targetData]);

  const updateTargetHandler = useMutation({
    mutationFn: async ({ data }) => {
      return await apiClient.targetAction({ data });
    },
    onSuccess: (data, vars) => {
      if ("isLast" in vars === false || ("isLast" in vars && vars["isLast"])) {
        const respTargetType = data.target_type;
        const respLabelKey = TARGET_TYPE_TO_LABEL_KEY[respTargetType] ?? "Target";
        resetActionFormFields();
        setSelectedTargets([]);

        successToast({
          message: t(($) => $.advertising["{targetType} Successfully Updated"], {
            targetType: t(($) => $.advertising.target_type[respLabelKey]),
          }),
        });
        invalidateTargetQueries(queryClient, channel.currentChannel);
      }
    },
    onError: (error) => {
      const responseData = error.response?.data;
      if (responseData && "error" in responseData) {
        const formattedError =
          responseData.error[0]?.split(":")[1] ?? t(($) => $.advertising["Error Updating Targets"]);
        errorToast({ message: formattedError, error });
        return;
      }

      const errTargetType = error.target_type;
      const errLabelKey = TARGET_TYPE_TO_LABEL_KEY[errTargetType] ?? "Target";
      errorToast({
        message: t(($) => $.advertising["Error Updating {targetType}"], {
          targetType: t(($) => $.advertising.target_type[errLabelKey]),
        }),
        error,
      });
    },
  });

  const scheduleHandler = useMutation({
    // The overlap refusal below is a rejection this flow explains, not a bug to report.
    meta: { expectedError: isScheduledActionOverlap },
    mutationFn: async (body) => {
      const reqData = body.data ?? {};

      return await api.post("/api/scheduledactions/", reqData);
    },

    onSuccess: (data, variables) => {
      if (!variables.isLast) {
        return;
      }
      successToast({
        message: t(($) => $.advertising["Successfully Scheduled Bid Updates"], { count: selectedTargets?.length ?? 1 }),
      });

      setSelectedTargets([]);
      setBidBoostFormData({
        actionUnit: "percent",
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().add(2, "days").format("YYYY-MM-DD"),
        keepBoostedBid: true,
      });
      queryClient.invalidateQueries();
    },
    onError: (error, vars) => {
      const conflictingRange = getScheduledActionConflictRange(error, dateRangeFmt);

      if (conflictingRange) {
        errorToast({
          message: t(
            ($) =>
              $.advertising[
                "Failed to create scheduled update. The selected dates overlap a scheduled action already running {dateRange}."
              ],
            { dateRange: conflictingRange },
          ),
          error,
        });
        return;
      }

      if (vars.isLast) {
        errorToast({
          message: t(($) => $.advertising["Failed to update Media Plans"], {
            count: vars.data.gvadvertisements_set.length,
          }),
        });
      }
      errorToast({
        message: t(($) => $.advertising["Failed to Schedule Bid Updates"], { count: selectedTargets?.length ?? 1 }),
        error,
      });
    },
  });

  const negateTargets = (selected) => {
    if (selected === "all") {
      dispatchBulkIfSelectAll({ action: "negate_target", values: [] });
      return;
    }
    const eligibleTargets = selected?.filter((targetId) => !importedTargetIds?.includes(targetId));
    const updateData = {
      target_ids: eligibleTargets,
      values: [],
      action: "negate_target",
      target_type: targetType,
      channel: channel.currentChannel ?? "amazon",
    };

    updateTargetHandler.mutate({ data: updateData });
  };

  const updateTargetState = (selected, newState) => {
    if (selected === "all") {
      dispatchBulkIfSelectAll({ action: "change_state", values: [newState] });
      return;
    }
    const updateData = {
      target_ids: selected,
      values: [newState],
      action: "change_state",
      target_type: targetType,
      channel: channel.currentChannel ?? "amazon",
    };
    updateTargetHandler.mutate({ data: updateData });
  };
  const updateTargetBids = (selected, bulkActionType) => {
    const actionData = { action: bulkActionType, values: [] };

    switch (bulkActionType) {
      case "set_bid_range":
        actionData.values = [parseBidOrNull(maxBid), parseBidOrNull(minBid)];
        break;
      case "clear_bid_range":
        actionData.action = "set_bid_range";
        actionData.values = [null, null];
        break;
      case "set_bid":
        actionData.values = [bidValue ? parseFloat(bidValue) : null];
        break;
      default:
        break;
    }

    if (selected === "all") {
      dispatchBulkIfSelectAll(actionData);
      return;
    }

    const requestData =
      actionData.action === "set_bid" ?
        { action: "set_bid", bids: Object.fromEntries(selected.map((targetId) => [targetId, actionData.values[0]])) }
      : { ...actionData, target_ids: selected };

    updateTargetHandler.mutate({
      data: { target_type: targetType, channel: channel.currentChannel ?? "amazon", ...requestData },
      // Single request, so mark true to launch success/failure notification
      isLast: true,
    });
  };

  const scheduleBidBoost = async (selected, actionType) => {
    const { actionValue, actionUnit, startDate, endDate, keepBoostedBid } = bidBoostFormData;

    if (selected === "all") {
      dispatchBulkIfSelectAll({
        action: "create_scheduled_actions",
        values: [
          {
            start_date: `${startDate.split("T")[0]}T00:00:00.000000`,
            end_date: `${endDate.split("T")[0]}T00:00:00.000000`,
            channel: channel.currentChannel === "walmart" ? "walmart" : "AMZ",
            fields: [
              {
                field_name:
                  actionType === "fixed" ? "manual_bid"
                  : actionUnit === "percent" ? "timebound_per_change"
                  : "timebound_flat_change",
                change_type: "set_change",
                change_value: getScheduledBidChangeValue(actionValue, actionUnit, actionType),
                rollback: !keepBoostedBid,
              },
            ],
          },
        ],
      });
      return;
    }

    for (let i = 0; i < selected?.length; i++) {
      let rollbackBid = true;

      if (keepBoostedBid) {
        rollbackBid = false;
      }

      const updateData = {
        channel: channel.currentChannel === "walmart" ? "walmart" : "AMZ",
        status: "scheduled",
        start_date: `${startDate.split("T")[0]}T00:00:00.000000`,
        end_date: `${endDate.split("T")[0]}T00:00:00.000000`,
        adv_keyword: selected[i],
        gvadvertisements_set: [],
        gvadvertisedkeyword_set: targetType === "keyword" && channel.currentChannel === "amazon" ? [selected[i]] : [],
        gvadvertisedtarget_set: targetType === "target" && channel.currentChannel === "amazon" ? [selected[i]] : [],
        walmartadgroupkeyword_set:
          targetType === "keyword" && channel.currentChannel === "walmart" ? [selected[i]] : [],
        walmartaditem_set: targetType === "target" && channel.currentChannel === "walmart" ? [selected[i]] : [],

        fields: [
          {
            field_name:
              actionType === "fixed" ? "manual_bid"
              : actionUnit === "percent" ? "timebound_per_change"
              : "timebound_flat_change",
            change_type: "set_change",
            // For percent bid boost values, convert into multiplier (i.e. 50% add to original becomes 1.5)
            change_value: getScheduledBidChangeValue(actionValue, actionUnit, actionType),
            rollback: rollbackBid,
          },
        ],
      };

      scheduleHandler.mutate({
        data: updateData,
        isLast: i === selected?.length - 1,
      });
    }
  };

  const deleteHandler = useMutation({
    mutationFn: async (data) => {
      const { id } = data;
      return await api.remove(`/api/scheduledactions/${id}/`);
    },

    onSuccess: (data, variables) => {
      if (!variables.isLast) {
        return;
      }
      successToast({
        message: t(($) => $.advertising["Successfully Cancelled Scheduled Bid Boosts"]),
      });
      setSelectedTargets([]);
      setBidBoostFormData({
        actionUnit: "percent",
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().add(2, "days").format("YYYY-MM-DD"),
        keepBoostedBid: true,
      });
      queryClient.invalidateQueries();
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.advertising["Failed to Cancel Scheduled Bid Boosts"]), error });
    },
  });

  const cancelScheduledBidBoosts = async (selected) => {
    if (selected === "all") {
      dispatchBulkIfSelectAll({ action: "delete_scheduled_actions", values: [true] });
      return;
    }

    for (let i = 0; i < selected?.length; i++) {
      const data = targetData?.keywords?.find((target) => target.target_id === selected[i]);

      // Filter out only scheduled and active ids

      const ids = data.scheduled_actions
        ?.filter((a) => ["scheduled", "active"].includes(a.scheduled_action_status))
        .map((a) => a.scheduled_action_id);

      for (let j = 0; j < ids.length; j++) {
        deleteHandler.mutate({ id: ids[j], isLast: i === selected.length - 1 });
      }
    }
  };

  const updatePlanTags = (selected, type = "add", tagIds) => {
    const action = type === "remove" ? "remove_tags" : "add_tags";
    const values = tagsEnhancedEnabled ? tagIds : filledTags;

    if (selected === "all") {
      dispatchBulkIfSelectAll({ action, values });
      return;
    }

    for (let i = 0; i < selected.length; i++) {
      updateTargetHandler.mutate({
        data: {
          target_ids: [selected[i]],
          values,
          action,
          target_type: targetType,
          channel: channel.currentChannel ?? "amazon",
        },
        isLast: i === selected.length - 1,
      });
    }
  };

  const resetActionFormFields = () => {
    setBidValue(null);
    setMinBid(null);
    setMaxBid(null);
    setStartDate(null);
    setEndDate(null);
    setFilledTags([]);
  };

  const bulkActionOptions = [
    {
      group: t(($) => $.advertising["Bids"]),
      actions: [
        {
          label: t(($) => $.advertising["Set Manual Bid"]),
          action: (selected) => updateTargetBids(selected, "set_bid"),
          actionFormFields: (
            <Row className="pb-5">
              <form>
                <p className="pb-4 fs-4">
                  {t(
                    ($) =>
                      $.advertising[
                        "Please enter your desired bid value for the selected targets using the field below."
                      ],
                    {
                      count: selectedTargets?.length ?? 1,
                      targetType: targetTypeLowercase,
                    },
                  )}
                </p>
                <Row className="pt-3 d-flex justify-content-center">
                  <Col xs={12} md={6} lg={4} className="px-5">
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      handleChildFormElement={(key, value) => {
                        setBidValue(value);
                      }}
                      label={t(($) => $.advertising["Bid Amount"])}
                      placeholder={t(($) => $.advertising["Enter a bid value"])}
                      defaultValue={bidValue}
                    />
                  </Col>
                </Row>
              </form>
            </Row>
          ),
          confirmationMessage: (
            <>
              {startDate > endDate ?
                <p className="py-2 text-danger fs-5">
                  {t(($) => $.advertising["Invalid date range. Start date must come before end date."])}
                </p>
              : parseFloat(bidValue) ?
                <p className="fs-4 py-2">
                  {t(
                    ($) =>
                      $.advertising["Are you sure you want to update bids for the selected targets to {bidValue}?"],
                    {
                      count: selectedTargets?.length ?? 1,
                      targetType: targetTypeLowercase,
                      bidValue: formatCurrency(bidValue, { marketPlace: marketPlace }),
                    },
                  )}
                </p>
              : null}
            </>
          ),
          disableConfirmButton: !parseFloat(bidValue),
        },
        {
          label: t(($) => $.advertising["Clear Manual Bid"]),
          action: (selected) => updateTargetBids(selected, "set_bid"),
          confirmationMessage: (
            <p className="fs-4 py-2">
              {t(($) => $.advertising["Are you sure you want to remove manual bids for the selected targets?"], {
                count: selectedTargets?.length ?? 1,
                targetType: targetTypeLowercase,
              })}
            </p>
          ),
        },
        {
          label: t(($) => $.advertising["Update Bid Ranges"], { count: selectedCount }),
          action: (selected) => updateTargetBids(selected, "set_bid_range"),
          actionFormFields: (
            <Row className="pb-5">
              <form>
                <Row className="d-flex justify-content-center align-items-center">
                  <p className="pb-4 fs-4">
                    {t(
                      ($) =>
                        $.advertising[
                          "Please enter your desired bid range for the selected targets using the fields below."
                        ],
                      {
                        count: selectedTargets?.length ?? 1,
                        targetType: targetTypeLowercase,
                      },
                    )}
                  </p>
                  <Col xs={12} md={6} lg={4} className="px-5">
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      handleChildFormElement={(key, value) => {
                        setMinBid(value);
                      }}
                      label={t(($) => $.advertising["Min Bid"])}
                      placeholder={t(($) => $.advertising["Enter a minimum bid"])}
                      defaultValue={minBid}
                    />
                  </Col>
                  <Col xs={12} md={6} lg={4} className="px-5">
                    <FormGroupTile
                      type="formcontrol"
                      formControlType={"number"}
                      handleChildFormElement={(key, value) => {
                        setMaxBid(value);
                      }}
                      label={t(($) => $.advertising["Max Bid"])}
                      placeholder={t(($) => $.advertising["Enter a maximum bid"])}
                      defaultValue={maxBid}
                    />
                  </Col>
                </Row>
              </form>
            </Row>
          ),
          confirmationMessage: (
            <>
              {parseFloat(minBid) >= parseFloat(maxBid) ?
                <p className="py-2 text-danger fs-5">
                  {t(
                    ($) =>
                      $.advertising[
                        "Invalid bid range. Please ensure the minimum bid is a lower value than the maximum bid."
                      ],
                  )}
                </p>
              : parseFloat(minBid) && parseFloat(maxBid) ?
                <p className="py-2 fs-4">
                  {t(
                    ($) =>
                      $.advertising[
                        "Are you sure you want to update the bid ranges for the selected targets to between {minBid} and {maxBid}?"
                      ],
                    {
                      count: selectedTargets?.length ?? 1,
                      targetType: targetTypeLowercase,
                      minBid: formatCurrency(minBid, { marketPlace: marketPlace }),
                      maxBid: formatCurrency(maxBid, { marketPlace: marketPlace }),
                    },
                  )}
                </p>
              : null}
            </>
          ),
          disableConfirmButton: !parseFloat(minBid) || !parseFloat(maxBid),
        },
        {
          label: t(($) => $.advertising["Remove Bid Ranges"], { count: selectedCount }),
          action: (selected) => updateTargetBids(selected, "clear_bid_range"),
          confirmationMessage: (
            <p className="py-2 fs-4">
              {t(($) => $.advertising["Are you sure you want to remove the bid ranges for the selected targets?"], {
                count: selectedTargets?.length ?? 1,
                targetType: targetTypeLowercase,
              })}
            </p>
          ),
        },
      ],
    },
    {
      group: t(($) => $.advertising["Scheduled Changes"]),
      actions: [
        {
          label: t(($) => $.advertising["Set Temporary {targetType} Bids"], {
            count: selectedCount,
            targetType: targetTypeLabel,
          }),
          action: (selected) => scheduleBidBoost(selected, "fixed"),
          confirmationMessage:
            (
              !parseFloat(bidBoostFormData?.actionValue) ||
              !bidBoostFormData?.actionUnit ||
              !bidBoostFormData?.startDate ||
              !bidBoostFormData?.endDate
            ) ?
              <p className="fs-4 py-2">{t(($) => $.advertising["Please fill out all required fields."])}</p>
            : <p className="fs-4 py-2">
                {t(
                  ($) =>
                    $.advertising[
                      "Are you sure you want to set the manual bid to {bidValue} for the selected targets from {startDate} to {endDate} ({hours} hours)?"
                    ],
                  {
                    count: selectedTargets?.length ?? 1,
                    targetType: targetTypeLowercase,
                    bidValue: formatCurrency(bidBoostFormData?.actionValue, { marketPlace: marketPlace }),
                    startDate: fmt.format(moment(bidBoostFormData?.startDate).toDate()),
                    endDate: fmt.format(moment(bidBoostFormData?.endDate).toDate()),
                    hours: (moment(bidBoostFormData?.endDate).diff(bidBoostFormData?.startDate, "days") + 1) * 24,
                  },
                )}
              </p>,
          actionFormFields: (
            <BulkAdPlanActionForm
              formData={bidBoostFormData}
              setFormData={setBidBoostFormData}
              actionTarget={"manual bid"}
              hideActionTypeOptions={true}
              hidePercentOption={true}
              renderDateFields={true}
              forceCurrencyOption={true}
              renderKeepBidBoostField={true}
            />
          ),
          disableConfirmButton:
            !parseFloat(bidBoostFormData?.actionValue) ||
            !bidBoostFormData?.actionUnit ||
            !bidBoostFormData?.startDate ||
            !bidBoostFormData?.endDate,
        },
        {
          label: t(($) => $.advertising["Schedule {targetType} Bid Boosts"], {
            count: selectedCount,
            targetType: targetTypeLabel,
          }),
          action: (selected) => scheduleBidBoost(selected, "boost"),
          confirmationMessage:
            (
              !parseFloat(bidBoostFormData?.actionValue) ||
              !bidBoostFormData?.actionUnit ||
              !bidBoostFormData?.startDate ||
              !bidBoostFormData?.endDate
            ) ?
              <p className="fs-4 py-2">{t(($) => $.advertising["Please fill out all required fields."])}</p>
            : <p className="fs-4 py-2">
                {t(
                  ($) =>
                    $.advertising[
                      "Are you sure you want to boost bids by {amount} for the selected targets from {startDate} to {endDate} ({hours} hours)?"
                    ],
                  {
                    count: selectedTargets?.length ?? 1,
                    targetType: targetTypeLowercase,
                    amount:
                      bidBoostFormData?.actionUnit === "percent" ?
                        `${bidBoostFormData?.actionValue}%`
                      : formatCurrency(bidBoostFormData?.actionValue, { marketPlace: marketPlace }),
                    startDate: fmt.format(moment(bidBoostFormData?.startDate).toDate()),
                    endDate: fmt.format(moment(bidBoostFormData?.endDate).toDate()),
                    hours: (moment(bidBoostFormData?.endDate).diff(bidBoostFormData?.startDate, "days") + 1) * 24,
                  },
                )}
              </p>,
          actionFormFields: (
            <BulkAdPlanActionForm
              formData={bidBoostFormData}
              setFormData={setBidBoostFormData}
              actionTarget={"bid boost"}
              hideActionTypeOptions={true}
              renderDateFields={true}
              renderKeepBidBoostField={true}
            />
          ),
          disableConfirmButton:
            !parseFloat(bidBoostFormData?.actionValue) ||
            !bidBoostFormData?.actionUnit ||
            !bidBoostFormData?.startDate ||
            !bidBoostFormData?.endDate,
        },
        {
          label: t(($) => $.advertising["Schedule {targetType} Bid Reductions"], {
            count: selectedCount,
            targetType: targetTypeLabel,
          }),
          action: (selected) => scheduleBidBoost(selected, "reduction"),
          confirmationMessage:
            (
              !parseFloat(bidBoostFormData?.actionValue) ||
              !bidBoostFormData?.actionUnit ||
              !bidBoostFormData?.startDate ||
              !bidBoostFormData?.endDate
            ) ?
              <p className="fs-4 py-2">{t(($) => $.advertising["Please fill out all required fields."])}</p>
            : <p className="fs-4 py-2">
                {t(
                  ($) =>
                    $.advertising[
                      "Are you sure you want to reduce bids by {amount} for the selected targets from {startDate} to {endDate} ({hours} hours)?"
                    ],
                  {
                    count: selectedTargets?.length ?? 1,
                    targetType: targetTypeLowercase,
                    amount:
                      bidBoostFormData?.actionUnit === "percent" ?
                        `${bidBoostFormData?.actionValue}%`
                      : formatCurrency(bidBoostFormData?.actionValue, { marketPlace: marketPlace }),
                    startDate: fmt.format(moment(bidBoostFormData?.startDate).toDate()),
                    endDate: fmt.format(moment(bidBoostFormData?.endDate).toDate()),
                    hours: (moment(bidBoostFormData?.endDate).diff(bidBoostFormData?.startDate, "days") + 1) * 24,
                  },
                )}
              </p>,
          actionFormFields: (
            <BulkAdPlanActionForm
              formData={bidBoostFormData}
              setFormData={setBidBoostFormData}
              actionTarget={"bid reduction"}
              hideActionTypeOptions={true}
              renderDateFields={true}
              renderKeepBidBoostField={true}
            />
          ),
          disableConfirmButton:
            !parseFloat(bidBoostFormData?.actionValue) ||
            !bidBoostFormData?.actionUnit ||
            !bidBoostFormData?.startDate ||
            !bidBoostFormData?.endDate,
        },
        {
          label: t(($) => $.advertising["Cancel Scheduled Bid Updates"]),
          action: (selected) => cancelScheduledBidBoosts(selected),
          confirmationMessage: (
            <p className="fs-4 py-2">
              {t(
                ($) =>
                  $.advertising[
                    "Are you sure you want to cancel scheduled bid boosts and reductions for the selected targets?"
                  ],
                {
                  count: selectedTargets?.length ?? 1,
                  targetType: targetTypeLowercase,
                },
              )}
            </p>
          ),
        },
      ],
    },
    {
      group: t(($) => $.advertising["Tags"]),
      actions:
        tagsEnhancedEnabled ?
          [
            {
              label: t(($) => $.advertising["Add {targetType} Tags"], { targetType: targetTypeLabel }),
              action: (selected) => {
                NiceModal.show(EntityTagsFormModal, {
                  entityType: "KEYWORD-TARGET",
                  title: t(($) => $.advertising["Add {targetType} Tags"], { targetType: targetTypeLabel }),
                  onSubmit: (tagIds) => updatePlanTags(selected, "add", tagIds),
                });
              },
              hidden: channel.currentChannel === "walmart",
            },
            {
              label: t(($) => $.advertising["Remove {targetType} Tags"], { targetType: targetTypeLabel }),
              action: (selected) => {
                NiceModal.show(EntityTagsFormModal, {
                  entityType: "KEYWORD-TARGET",
                  title: t(($) => $.advertising["Remove {targetType} Tags"], { targetType: targetTypeLabel }),
                  onSubmit: (tagIds) => updatePlanTags(selected, "remove", tagIds),
                });
              },
              hidden: channel.currentChannel === "walmart",
            },
          ]
        : [
            {
              label: t(($) => $.advertising["Add {targetType} Tags"], { targetType: targetTypeLabel }),
              action: (selected) => {
                updatePlanTags(selected);
              },
              actionFormFields: (
                <BulkTagsForm
                  filledTags={filledTags}
                  setFilledTags={setFilledTags}
                  selected={selectedTargets}
                  suggestedTags={customTags}
                  taggedItemDescription={targetType}
                  actionType={"add"}
                />
              ),
              confirmationMessage: (
                <p>
                  {t(($) => $.advertising["Are you sure you want to add these tags from the selected media plans?"], {
                    tagCount: filledTags?.length ?? 0,
                    count: selectedTargets?.length ?? 1,
                  })}
                </p>
              ),
              disableConfirmButton: !filledTags.length,
              hidden: channel.currentChannel === "walmart",
            },
            {
              label: t(($) => $.advertising["Remove {targetType} Tags"], { targetType: targetTypeLabel }),
              action: (selected) => {
                updatePlanTags(selected, "remove");
              },
              actionFormFields: (
                <BulkTagsForm
                  filledTags={filledTags}
                  setFilledTags={setFilledTags}
                  selected={selectedTargets}
                  suggestedTags={customTags}
                  taggedItemDescription={targetType}
                  actionType={"add"}
                />
              ),
              confirmationMessage: (
                <p>
                  {t(
                    ($) => $.advertising["Are you sure you want to remove these tags from the selected media plans?"],
                    {
                      tagCount: filledTags?.length ?? 0,
                      count: selectedTargets?.length ?? 1,
                    },
                  )}
                </p>
              ),
              disableConfirmButton: !filledTags.length,
              hidden: channel.currentChannel === "walmart",
            },
          ],
    },
  ];

  const trellisManagedOptions = [
    {
      group: t(($) => $.advertising["State"]),
      actions: [
        {
          label:
            targetType === "target" ?
              t(($) => $.advertising["Pause Targets"], { count: selectedCount })
            : t(($) => $.advertising["Pause Keywords"], { count: selectedCount }),
          action: (selected) => updateTargetState(selected, TARGET_STATES.PAUSED.value),
          confirmationMessage: (
            <p className="fs-4 py-2">
              {t(($) => $.advertising["Are you sure you want to pause the selected targets?"], {
                count: selectedTargets?.length ?? 1,
                targetType: targetTypeLowercase,
              })}
            </p>
          ),
        },
        {
          label:
            targetType === "target" ?
              t(($) => $.advertising["Enable Targets"], { count: selectedCount })
            : t(($) => $.advertising["Enable Keywords"], { count: selectedCount }),
          action: (selected) => updateTargetState(selected, TARGET_STATES.ENABLED.value),
          confirmationMessage: (
            <p>
              {t(($) => $.advertising["Are you sure you want to enable the selected targets?"], {
                count: selectedTargets?.length ?? 1,
                targetType: targetTypeLowercase,
              })}
            </p>
          ),
        },
        {
          label:
            targetType === "target" ?
              t(($) => $.advertising["Archive Targets"], { count: selectedCount })
            : t(($) => $.advertising["Archive Keywords"], { count: selectedCount }),
          action: (selected) => updateTargetState(selected, TARGET_STATES.ARCHIVED.value),
          confirmationMessage: (
            <p>
              {t(($) => $.advertising["Are you sure you want to archive the selected targets?"], {
                count: selectedTargets?.length ?? 1,
                targetType: targetTypeLowercase,
              })}
            </p>
          ),
        },
      ],
    },
  ];

  const negationOptions = [
    {
      group: t(($) => $.advertising["Negation"]),
      actions: [
        {
          label:
            targetType === "target" ?
              t(($) => $.advertising["Negate Targets"], { count: selectedCount })
            : t(($) => $.advertising["Negate Keywords"], { count: selectedCount }),
          action: (selected) => negateTargets(selected),
          confirmationMessage: (
            <>
              <p className="fs-4 py-2">
                {t(($) => $.advertising["Are you sure you want to negate the selected targets?"], {
                  count: selectedTargets?.length ?? 1,
                  targetType: targetTypeLowercase,
                })}
              </p>
              {skippedTargetCount ?
                <p className="fs-4 py-2">
                  {t(
                    ($) =>
                      $.advertising[
                        "{skippedTargetCount} imported targets will be ignored that are ineligible for negation."
                      ],
                    {
                      skippedTargetCount,
                      count: skippedTargetCount,
                    },
                  )}
                </p>
              : null}
            </>
          ),
        },
      ],
    },
  ];

  if (isTrellisManagedAd) {
    bulkActionOptions.unshift(...trellisManagedOptions);
  }

  if (!isNTMAd) {
    bulkActionOptions.push(...negationOptions);
  }

  return { bulkActionOptions: bulkActionOptions ?? [], resetActionFormFields };
};

export default useTargetBulkActions;
